import React from 'react';
import {
  SecurityScreen,
  type SecurityVariantConfig,
  qFullName,
  qDob,
  qMothersMaidenName,
  qMobileNumber,
  qRecurringPayment,
  qRecentTransaction,
  qJointAccounts,
  qVisaCreditLimit,
  qVisaCardholders,
  qVisaCardExpiry,
  qPropertyAddress,
  qMortgageYear,
  qMortgageTerm,
  qMortgageBrokerOrBranch,
  qHomeInsurance,
  qLifeAssurance,
  qDirectDebitBank,
  qMortgageRepaymentProtection,
  qFirstSchool,
  qFavouriteMusic,
  qEyeColour,
} from './SecurityScreen.js';

/**
 * The seven Security variants (SCRN 4..10), faithful to the customer's Figma
 * designs and the CCF acceptance criteria (CCF wins over stale Figma). Each is
 * the same shared {@link SecurityScreen} parameterised with the variant's
 * heading, identity questions, the granted verification level and the 2FA
 * requirement.
 *
 * Identity-question sourcing per CCF:
 *   - VISA credit-card facts (limit, cardholders, expiry) come from TSYS;
 *   - mortgage facts (address, year, term, providers) come from Hogan;
 *   - EDS/BUS24 facts come from Unibanks.
 * All variants carry the mandatory 2FA OTP step (CCF-16) EXCEPT the Authorised
 * VISA User journey (CCF-25), which verifies name + DoB + card expiry against
 * TSYS only.
 *
 * CCF-53: the level GRANTED on completion is per-variant — medium variants
 * (SCRN-04/05) grant `'medium'`; the high variants grant `'high'`. (The shared
 * screen previously hard-coded `'high'`, over-granting the medium VISA flow.)
 */
export const SECURITY_VARIANTS: Record<string, SecurityVariantConfig> = {
  // SCRN-4 — VISA Medium risk (CCF-53): name + DoB + 2FA. Grants MEDIUM.
  'SCRN-04': {
    screenId: 'SCRN-04',
    heading: 'Visa: medium risk',
    verificationLabel: 'Visa',
    grantedLevel: 'medium',
    requires2fa: true,
    questions: [qFullName, qDob],
    // Issue #101: medium page → offers the other medium pages + VISA High.
    mediumNav: true,
  },

  // SCRN-5 — Business 24 Medium risk (CCF-23): name + DoB (mandatory) + the
  // grouped "confirm any 1 of 3" knowledge questions (first school, favourite
  // music, eye colour) + 2FA. Grants MEDIUM.
  'SCRN-05': {
    screenId: 'SCRN-05',
    heading: 'Business: Medium Security',
    verificationLabel: 'Business',
    grantedLevel: 'medium',
    requires2fa: true,
    mandatoryHeading: 'Mandatory selection',
    questions: [qFullName, qDob],
    knowledgeGroup: {
      heading: 'Select any 1 of 3',
      questions: [qFirstSchool, qFavouriteMusic, qEyeColour],
      // Issue #334: the real gateway never populates FirstSchool/
      // FavouriteMusic/EyeColour individually (mock-only convenience fields,
      // issue #76) — only the unordered free-text `AdditionalQuestionAnswers`
      // (mirrors registration's #305 ruling for the same BUS24 data).
      additionalAnswersUnordered: true,
    },
    // Issue #101: medium page → offers the other medium pages + BUS24 High.
    mediumNav: true,
  },

  // SCRN-6 — Authorised VISA User (CCF-25): the agent captures the 16-digit Visa
  // Credit Card number, then verifies name + DoB + card expiry against TSYS
  // (all three must pass). No 2FA. High-security flow → grants HIGH.
  'SCRN-06': {
    screenId: 'SCRN-06',
    heading: 'Visa: authorised user',
    verificationLabel: 'Visa',
    grantedLevel: 'high',
    requires2fa: false,
    sourceNote: 'Verify the following against TSYS.',
    cardNumberEntry: '16-digit Visa Credit Card number',
    questions: [qFullName, qDob, qVisaCardExpiry],
    // Issue #117: high page → dropdown offers all the medium security options.
    highNav: true,
  },

  // SCRN-7 — EDS High security (CCF-26): name, date of birth, mother's maiden
  // name (with the "not on file" branch), mobile (with the "not on file"
  // branch), recurring payment, joint accounts + 2FA. Grants HIGH.
  // Issue #563: DoB was missing from this list (an original omission — it was
  // never present here, unlike the sibling variants) though CCF-26 AC lists it
  // ("System displays date of birth; agent marks Pass or Fail", Happy Path #1
  // step 2). Added right after full name, per the CCF-26 question ordering.
  'SCRN-07': {
    screenId: 'SCRN-07',
    heading: 'EDS: high security',
    verificationLabel: 'EDS',
    grantedLevel: 'high',
    requires2fa: true,
    questions: [
      qFullName,
      qDob,
      qMothersMaidenName,
      qMobileNumber,
      qRecurringPayment,
      qJointAccounts,
    ],
    // Issue #117: high page → dropdown offers all the medium security options.
    highNav: true,
  },

  // SCRN-8 — VISA High security (CCF-5): name, mother's maiden name (with the
  // "not on file" branch), mobile (with the "not on file" branch), credit
  // limit, other cardholders, recent transaction (TSYS) + 2FA. Grants HIGH.
  'SCRN-08': {
    screenId: 'SCRN-08',
    heading: 'Visa: high security',
    verificationLabel: 'Visa',
    grantedLevel: 'high',
    requires2fa: true,
    questions: [
      qFullName,
      qMothersMaidenName,
      qMobileNumber,
      qVisaCreditLimit,
      qVisaCardholders,
      qRecentTransaction,
    ],
    // Issue #117: high page → dropdown offers all the medium security options.
    highNav: true,
  },

  // SCRN-9 — Mortgage security (CCF-24): 2 mandatory questions (name + property
  // address) plus "choose any 2 of 7" Hogan questions + 2FA. Grants HIGH. The
  // Direct-Debit-bank question is only selectable when the customer has a DD.
  'SCRN-09': {
    screenId: 'SCRN-09',
    heading: 'Mortgage security',
    verificationLabel: 'Mortgage',
    grantedLevel: 'high',
    requires2fa: true,
    mandatoryHeading: 'Mandatory selection',
    questions: [qFullName, qPropertyAddress],
    chooseGroup: {
      heading: 'Select any 2 of 7: Please check Hogan',
      chooseCount: 2,
      questions: [
        qMortgageYear,
        qMortgageTerm,
        qMortgageBrokerOrBranch,
        qDirectDebitBank,
        qHomeInsurance,
        qLifeAssurance,
        qMortgageRepaymentProtection,
      ],
    },
    // Issue #117: high page → dropdown offers all the medium security options.
    highNav: true,
  },

  // SCRN-10 — Business 24 High security (CCF-42): name, DoB, mother's maiden
  // name (with the "not on file" branch), mobile (with the "not on file"
  // branch), and knowledge questions (first school / favourite music), each
  // with individual Pass/Fail (#722 AC change) + 2FA. Grants HIGH.
  'SCRN-10': {
    screenId: 'SCRN-10',
    heading: 'Business: High Security',
    verificationLabel: 'Business',
    grantedLevel: 'high',
    requires2fa: true,
    questions: [qFullName, qDob, qMothersMaidenName, qMobileNumber],
    knowledgeGroup: {
      heading: 'Select any 1 of 2',
      questions: [qFirstSchool, qFavouriteMusic],
      // Issue #334: same real-gateway gap as SCRN-05 — fall back to the
      // unordered free-text `AdditionalQuestionAnswers` list.
      additionalAnswersUnordered: true,
      // Issue #722: AC change — ask both questions with individual Pass/Fail.
      askAll: true,
    },
    // Issue #117: high page → dropdown offers all the medium security options.
    highNav: true,
  },
};

/** Renders the shared security screen for a given variant id. */
export function securityVariantElement(screenId: string): React.JSX.Element {
  const config = SECURITY_VARIANTS[screenId];
  if (!config) {
    throw new Error(`No security variant config for ${screenId}`);
  }
  return <SecurityScreen config={config} />;
}
